import { Edit, Sms } from "@mui/icons-material"
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material"
import Joi from "joi"
import { map, noop, pick } from "lodash"
import { FC, useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { SmsExpeditor } from "shared/smsExpeditor"
import {
  SmsExpeditorSuppliers,
  SmsExpeditorThingTypeMrn,
} from "shared/smsExpeditorData"
import { Tenant } from "shared/tenant"
import { TenantThingTypeMrn } from "shared/tenantData"
import { useAppSelector } from "src/app/hooks"
import {
  useCreateThingsMutation,
  useDeleteThingsMutation,
  useUpdateThingsMutation,
} from "src/features/things/thingsApi"
import { selectSaList } from "src/features/things/thingsSlice"
import FormFields, { FormField } from "./FormFields"
import { SaTenantExpeditorSelectorDialog } from "./SaTenantExpeditorSelectorDialog"
import { Sl2Btn } from "./Sl2Btn"
import { Sl2FormContainer } from "./Sl2FormContainer"
import SmsExpeditorTenantCard from "./SmsExpeditorTenantCard"

const joiSchemaInfo = Joi.object({
  name: Joi.string().optional(),
  description: Joi.string().optional(),
  fromNumber: Joi.string().optional(),
  supplier: Joi.string()
    .valid(SmsExpeditorSuppliers.TWILIO, SmsExpeditorSuppliers.SOMUM)
    .optional(),
  messagingServiceSid: Joi.string()
    .pattern(/^MG[0-9a-f]{32}$/)
    .optional()
    .allow(null, ""),
})

type SmsExpeditorCardProps = {
  smsExpeditor: SmsExpeditor | undefined
  mode?: "edit" | "create"
  onComplete?: () => void
}

export const SmsExpeditorCard: FC<SmsExpeditorCardProps> = ({
  smsExpeditor,
  mode = "edit",
  onComplete = noop,
}) => {
  const { t } = useTranslation()
  const [isEditing, setIsEditing] = useState(false)
  const [addingTenant, setAddingTenant] = useState(false)
  const [updateThings, { isLoading: isUpdating }] = useUpdateThingsMutation()
  const [createThings, { isLoading: isCreating }] = useCreateThingsMutation()
  const [deleteThings] = useDeleteThingsMutation()

  const [snackMessage, setSnackMessage] = useState({
    message: "",
    severity: "success" as "success" | "error" | "warning" | "info" | undefined,
  })

  const formFields = useMemo(() => {
    return [
      {
        name: "name",
        fieldType: "text",
        label: t("name"),
        required: true,
        fullWidth: true,
        size: "small",
        gridProps: { xs: 12 },
      },
      // description
      {
        name: "description",
        fieldType: "text",
        label: t("description"),
        required: true,
        fullWidth: true,
        size: "small",
        gridProps: { xs: 12 },
      },
      // fromNumber
      {
        name: "fromNumber",
        fieldType: "text",
        label: t("fromNumber"),
        required: true,
        fullWidth: true,
        size: "small",
        gridProps: { xs: 12 },
      },
      // supplier
      {
        name: "supplier",
        fieldType: "select",
        label: t("supplier"),
        required: true,
        fullWidth: true,
        size: "small",
        gridProps: { xs: 12 },
        options: [
          { id: SmsExpeditorSuppliers.TWILIO, label: "Twilio" },
          { id: SmsExpeditorSuppliers.SOMUM, label: "Somum" },
        ],
      },
      // messagingServiceSid (Twilio Messaging Service for RCS routing)
      {
        name: "messagingServiceSid",
        fieldType: "text",
        label: t("messagingServiceSid"),
        required: false,
        fullWidth: true,
        size: "small",
        gridProps: { xs: 12 },
        textProps: {
          placeholder: "MGxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx",
        },
      },
    ] as FormField[]
  }, [t])

  const onSubmit = useCallback(
    async (data: any) => {
      try {
        const toMutate = {
          ...data,
          id: smsExpeditor?.id,
          thingTypeMrn: SmsExpeditorThingTypeMrn,
        }
        const mutate = mode === "edit" ? updateThings : createThings
        await mutate({
          body: { things: [toMutate] },
          // params: { tenantId },
        }).unwrap()
        setSnackMessage({
          message: t(
            mode === "edit"
              ? "itemUpdatedSuccessfully"
              : "itemCreatedSuccessfully",
          ),
          severity: "success",
        })
      } catch (error) {
        console.log(error)
        setSnackMessage({
          message: t(mode === "edit" ? "itemUpdatedError" : "itemCreatedError"),
          severity: "error",
        })
      } finally {
        setIsEditing(false)
        onComplete()
      }
    },
    [createThings, mode, onComplete, smsExpeditor?.id, t, updateThings],
  )

  const onDelete = useCallback(async () => {
    try {
      const toDelete = {
        id: smsExpeditor?.id,
        thingTypeMrn: SmsExpeditorThingTypeMrn,
      }

      await deleteThings({
        body: { things: [toDelete] },
        // params: { tenantId },
      }).unwrap()

      setSnackMessage({
        message: t("itemDeletedSuccessfully"),
        severity: "success",
      })
    } catch (error) {
      console.log(error)
      setSnackMessage({
        message: t("itemDeletedError"),
        severity: "error",
      })
    } finally {
      setIsEditing(false)
      onComplete()
    }
  }, [deleteThings, onComplete, smsExpeditor?.id, t])

  const tenants = useAppSelector((state) => selectSaList<Tenant>(state)).filter(
    (t) =>
      t.thingTypeMrn === TenantThingTypeMrn &&
      t.smsExpeditorId === smsExpeditor?.id,
  )

  const [showRemoveTenantConfirmDialog, setShowRemoveTenantConfirmDialog] =
    useState(false)
  const [tenantToRemove, setTenantToRemove] = useState<Tenant | undefined>(
    undefined,
  )
  const [
    showRemoveExpeditorConfirmDialog,
    setShowRemoveExpeditorConfirmDialog,
  ] = useState(false)
  const onRemoveAssociatedTenant = useCallback(async () => {
    try {
      if (!tenantToRemove) {
        console.error("No tenant to remove")
        return
      }
      // remove tenant from expeditor
      const toUpdate = {
        id: tenantToRemove.id,
        thingTypeMrn: TenantThingTypeMrn,
        smsExpeditorId: "",
      }
      await updateThings({
        body: { things: [toUpdate] },
      }).unwrap()
      setSnackMessage({
        message: t("itemUpdatedSuccessfully"),
        severity: "success",
      })
    } catch (error) {
      console.log(error)
      setSnackMessage({
        message: t("itemUpdatedError"),
        severity: "error",
      })
    } finally {
      setShowRemoveTenantConfirmDialog(false)
      setTenantToRemove(undefined)
    }
  }, [t, tenantToRemove, updateThings])
  return (
    <Paper data-ui="UI-SL2W-181"
      elevation={2}
      sx={{
        p: 3,
        pb: 4,
        maxWidth: 600,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        background: "linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)",
      }}
    >
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Sms sx={{ color: "primary.main", fontSize: "1.8rem" }} />
          <Typography variant="h5" fontWeight={600} color="text.primary">
            {t(mode === "create" ? "newSmsExpeditor" : "smsExpeditor")}
          </Typography>
        </Stack>
        {!isEditing && mode !== "create" && (
          <IconButton
            onClick={() => setIsEditing(true)}
            sx={{
              color: "primary.main",
              "&:hover": {
                backgroundColor: "primary.light",
                color: "primary.contrastText",
              },
            }}
          >
            <Edit />
          </IconButton>
        )}
      </Stack>
      <Divider sx={{ mb: 3 }} />

      <Stack direction="column" spacing={3}>
        {!isEditing && smsExpeditor && mode !== "create" && (
          <Box sx={{ py: 1 }}>
            <Stack direction="column" spacing={2}>
              <Box>
                <Typography
                  variant="h6"
                  fontWeight={600}
                  color="text.primary"
                  gutterBottom
                >
                  {smsExpeditor.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {smsExpeditor.description}
                </Typography>
              </Box>
            </Stack>
          </Box>
        )}
        {(isEditing || mode === "create") && (
          <Box sx={{ py: 2, px: 1 }}>
            <Sl2FormContainer
              joiSchema={joiSchemaInfo}
              defaultValues={pick(smsExpeditor, [
                "name",
                "description",
                "fromNumber",
                "supplier",
                "messagingServiceSid",
              ])}
              onError={(errors: any) => console.log(errors)}
              onSuccess={onSubmit}
            >
              <FormFields fields={formFields} />
              <Stack
                direction="row"
                spacing={2}
                justifyContent="flex-end"
                sx={{
                  mt: 3,
                  pt: 2,
                  borderTop: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Sl2Btn
                  onClick={() => setIsEditing(false)}
                  disabled={isUpdating}
                  variant="outlined"
                  sx={{ minWidth: 120 }}
                >
                  {t("cancel")}
                </Sl2Btn>
                <Sl2Btn
                  type="submit"
                  disabled={isUpdating}
                  variant="contained"
                  sx={{ minWidth: 120 }}
                >
                  {t(mode === "create" ? "create" : "modify")}
                </Sl2Btn>
              </Stack>
            </Sl2FormContainer>
          </Box>
        )}
        {mode !== "create" && (
          <>
            <Box sx={{ py: 2 }}>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                color="text.primary"
                gutterBottom
              >
                {t("fromNumber")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t("fromNumberDescription")}
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  backgroundColor: "grey.50",
                  borderColor: "grey.200",
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Sms sx={{ color: "action.active", fontSize: "1.5rem" }} />
                  <Typography variant="body1" fontWeight={500}>
                    {smsExpeditor?.fromNumber}
                  </Typography>
                </Stack>
              </Paper>
            </Box>

            <Box sx={{ py: 2 }}>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                color="text.primary"
                gutterBottom
              >
                {t("supplier")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t("supplierDescription")}
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  backgroundColor: "grey.50",
                  borderColor: "grey.200",
                }}
              >
                <Typography variant="body1" fontWeight={500}>
                  {smsExpeditor?.supplier === SmsExpeditorSuppliers.TWILIO
                    ? "Twilio"
                    : smsExpeditor?.supplier === SmsExpeditorSuppliers.SOMUM
                      ? "Somum"
                      : smsExpeditor?.supplier || "Twilio"}
                </Typography>
              </Paper>
            </Box>

            {smsExpeditor?.messagingServiceSid && (
              <Box sx={{ py: 2 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  color="text.primary"
                  gutterBottom
                >
                  {t("messagingServiceSid")}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t("messagingServiceSidDescription")}
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    backgroundColor: "grey.50",
                    borderColor: "grey.200",
                  }}
                >
                  <Typography
                    variant="body1"
                    fontWeight={500}
                    sx={{ fontFamily: "monospace" }}
                  >
                    {smsExpeditor.messagingServiceSid}
                  </Typography>
                </Paper>
              </Box>
            )}

            <Box sx={{ py: 2 }}>
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 2 }}
              >
                <Box>
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    color="text.primary"
                  >
                    {t("associatedOrgs")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("smsExpeditorAssociatedOrgsDescription")}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    fontWeight={500}
                  >
                    ({tenants?.length})
                  </Typography>
                  <Sl2Btn
                    variant="outlined"
                    size="small"
                    onClick={() => setAddingTenant(true)}
                    sx={{ minWidth: 80 }}
                  >
                    {t("add")}
                  </Sl2Btn>
                </Box>
              </Stack>
              <Box sx={{ pl: 1 }}>
                {tenants?.length > 0 ? (
                  <Stack direction="column" spacing={1.5}>
                    {map(tenants, (tenant) => (
                      <SmsExpeditorTenantCard
                        tenant={tenant}
                        key={tenant.id}
                        onRemove={() => {
                          setTenantToRemove(tenant)
                          setShowRemoveTenantConfirmDialog(true)
                        }}
                      />
                    ))}
                  </Stack>
                ) : (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 3,
                      textAlign: "center",
                      backgroundColor: "grey.50",
                      borderColor: "grey.200",
                      borderStyle: "dashed",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {t("noAssociatedOrgs")}
                    </Typography>
                  </Paper>
                )}
              </Box>
            </Box>
            <Box
              sx={{
                py: 3,
                mt: 2,
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography
                variant="subtitle1"
                fontWeight={600}
                color="text.primary"
                gutterBottom
              >
                {t("deleteExpeditor")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t("deleteExpeditorDescription")}
              </Typography>
              <Sl2Btn
                variant="outlined"
                color="error"
                disabled={tenants.length > 0}
                onClick={() => setShowRemoveExpeditorConfirmDialog(true)}
                sx={{
                  "&:hover": {
                    backgroundColor: "error.main",
                    color: "error.contrastText",
                  },
                }}
              >
                {t("delete")}
              </Sl2Btn>
            </Box>
          </>
        )}
        {/* typoe grpah codefor id */}
        <Box
          sx={{ mt: 3, pt: 2, borderTop: "1px solid", borderColor: "divider" }}
        >
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ fontFamily: "monospace" }}
          >
            ID: {smsExpeditor?.id}
          </Typography>
        </Box>
      </Stack>
      {addingTenant && smsExpeditor?.id && (
        <SaTenantExpeditorSelectorDialog
          expeditorId={smsExpeditor.id}
          expeditorKey="smsExpeditorId"
          onClose={() => setAddingTenant(false)}
          onComplete={() => setAddingTenant(false)}
        />
      )}
      <Snackbar
        open={!!snackMessage?.message}
        autoHideDuration={6000}
        onClose={() => setSnackMessage({ message: "", severity: undefined })}
      >
        <Alert severity={snackMessage.severity}>{snackMessage.message}</Alert>
      </Snackbar>
      {showRemoveTenantConfirmDialog && (
        <Dialog data-ui="UI-SL2W-183"
          open={true}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 2, p: 1 },
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Typography variant="h6" fontWeight={600}>
              {t("disassociateExpeditorTenant")}
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ minHeight: 80, py: 1 }}>
            <DialogContentText>
              {t("disassociateExpeditorTenantConfirmMessage")}
            </DialogContentText>
          </DialogContent>
          <Divider />
          <DialogActions sx={{ p: 2, pt: 1.5 }}>
            <Sl2Btn
              onClick={() => {
                setShowRemoveTenantConfirmDialog(false)
                setTenantToRemove(undefined)
              }}
              variant="outlined"
              sx={{ minWidth: 80 }}
            >
              {t("cancel")}
            </Sl2Btn>
            <Sl2Btn
              onClick={() => {
                onRemoveAssociatedTenant()
              }}
              variant="contained"
              color="error"
              sx={{ minWidth: 80 }}
            >
              {t("dissociate")}
            </Sl2Btn>
          </DialogActions>
        </Dialog>
      )}
      {showRemoveExpeditorConfirmDialog && (
        <Dialog data-ui="UI-SL2W-184"
          open={true}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 2, p: 1 },
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Typography variant="h6" fontWeight={600}>
              {t("deleteExpeditor")}
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ minHeight: 80, py: 1 }}>
            <DialogContentText>
              {t("deleteExpeditorConfirmMessage")}
            </DialogContentText>
          </DialogContent>
          <Divider />
          <DialogActions sx={{ p: 2, pt: 1.5 }}>
            <Sl2Btn
              onClick={() => {
                setShowRemoveExpeditorConfirmDialog(false)
              }}
              variant="outlined"
              sx={{ minWidth: 80 }}
            >
              {t("cancel")}
            </Sl2Btn>
            <Sl2Btn
              onClick={() => {
                onDelete()
              }}
              variant="contained"
              color="error"
              sx={{ minWidth: 80 }}
            >
              {t("delete")}
            </Sl2Btn>
          </DialogActions>
        </Dialog>
      )}
    </Paper>
  )
}

export default SmsExpeditorCard
